import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize } from 'rxjs/operators';
import { LanguageService, ToastService } from '../../core';
import { IPlanSendControl, PlanSendControl, planSendErrorMsgs, PlanSendForm, username_caseVersionMismatch, username_notEligible_fitbone, username_notEligible_jps, username_notEligible_tlhex, username_notExisting } from '../../models';
import { SendService } from '../../services';
import { BaseComponent, FormValidationMsgDirective } from '../../shared';


/**
 * Modal component to send plan.
 */
@Component({
	selector: 'plan-send-modal',
	templateUrl: './plan-send-modal.component.html',
	imports: [
		FormsModule,
		ReactiveFormsModule,
		FormValidationMsgDirective
	],
	changeDetection: ChangeDetectionStrategy.Eager
})
export class PlanSendModalComponent extends BaseComponent implements OnInit {

	public activeModal = inject(NgbActiveModal);
	private readonly sendSrv = inject(SendService);
	private readonly toastSrv = inject(ToastService);
	protected override readonly langSrv = inject(LanguageService);

	@Input() planGuid = '';
	@Input() userGuid = '';

	planId = '';
	isSaving = false;
	username_caseVersionMismatch = username_caseVersionMismatch;
	caseVersionMismatchError = '';

	readonly sendForm = new FormGroup<IPlanSendControl>(new PlanSendControl());
	readonly errorMsgs = planSendErrorMsgs;

	ngOnInit(): void {
		this.sendSrv.getPlan(this.planGuid).subscribe(plan => this.planId = plan.number);
		this.sendForm.controls.planGuid.setValue(this.planGuid);
		this.sendForm.controls.userGuid.setValue(this.userGuid);
	}

	/**
	* Trim username input field.
	*/
	trimUserName(): void {
		const username = this.sendForm.controls.userName;
		if (username.value) {
			username.setValue(username.value.trim());
		}
	}

	/**
	* Submit form and handle response messages.
	*/
	confirm(): void {
		if (this.sendForm.valid && !this.isSaving) {
			this.isSaving = true;
			this.sendSrv.sendForm(this.sendForm.value as PlanSendForm).pipe(
				finalize(() => this.isSaving = false)
			).subscribe({
				next: () => {
					this.toastSrv.showSuccess(this.labels().PLAN_SEND_MODAL_COMPONENT_SUCCESS_MESSAGE);
					this.activeModal.close();
				},
				error: err => {
					this.toastSrv.showError(this.labels().PLAN_SEND_MODAL_COMPONENT_ERROR_MESSAGE);
					this.handleSendError(err);
				}
			});
		}
	}

	private handleSendError(err: Error): void {
		let errorCode = '';
		switch (err.message) {
			case 'UserNotFound': errorCode = username_notExisting; break;
			case 'UserNotJpsProd': errorCode = username_notEligible_jps; break;
			case 'UserNotFitboneProd': errorCode = username_notEligible_fitbone; break;
			case 'UserNotTlhexProd': errorCode = username_notEligible_tlhex; break;
			case 'CaseVersionMismatch': {
				errorCode = username_caseVersionMismatch;
				this.caseVersionMismatchError = this.labels().VALIDATION_MESSAGE_CASEVERSIONMISMATCH_USERNAME.replace('{{1}}', this.sendForm.value.userName);
				break;
			}
		}
		this.sendForm.controls.userName.setErrors({ [`${errorCode}`]: true });
	}

}
